import React, { useState } from 'react';
import { createDataSource, uploadDataFile, getExcelSheets, DataSource } from '../api/client';

interface DataSourceConfigProps {
  onSuccess: (dataSource: DataSource) => void;
}

const DataSourceConfig: React.FC<DataSourceConfigProps> = ({ onSuccess }) => {
  const [sourceType, setSourceType] = useState<'json' | 'csv' | 'excel' | 'database' | 'api'>('json');
  const [name, setName] = useState('');
  const [config, setConfig] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [loadingSheets, setLoadingSheets] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setUploading(true);
    setError(null);
    setAvailableSheets([]);

    try {
      const result = await uploadDataFile(file);
      setConfig({ ...config, file_path: result.file_path });
      setUploading(false);

      // For Excel files, load available sheet names
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setLoadingSheets(true);
        try {
          const sheets = await getExcelSheets(result.file_path);
          setAvailableSheets(sheets);
          // Auto-select first sheet if available
          if (sheets.length > 0) {
            setConfig((prev: Record<string, any>) => ({ ...prev, file_path: result.file_path, sheet_name: sheets[0] }));
          }
        } catch (sheetErr) {
          // Error silently handled
        } finally {
          setLoadingSheets(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload file');
      setUploading(false);
      setSelectedFile(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const dataSource = await createDataSource({
        name,
        type: sourceType,
        config,
        is_template: true, // Datenvorlagen speichern immer nur die Struktur
      });
      onSuccess(dataSource);
      // Reset form
      setName('');
      setConfig({});
      setSelectedFile(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create data source');
    } finally {
      setLoading(false);
    }
  };

  const renderConfigFields = () => {
    switch (sourceType) {
      case 'json':
        return (
          <div className="config-field">
            <label>JSON-Datei:</label>
            <input
              type="file"
              accept=".json"
              onChange={handleFileChange}
              disabled={uploading}
              required
            />
            {uploading && <p style={{ color: '#667eea', fontSize: '0.9rem' }}>Uploading...</p>}
            {selectedFile && !uploading && (
              <p style={{ color: '#4caf50', fontSize: '0.9rem' }}>
                ✓ {selectedFile.name} uploaded
              </p>
            )}
          </div>
        );

      case 'csv':
        return (
          <>
            <div className="config-field">
              <label>CSV-Datei:</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={uploading}
                required
              />
              {uploading && <p style={{ color: '#667eea', fontSize: '0.9rem' }}>Uploading...</p>}
              {selectedFile && !uploading && (
                <p style={{ color: '#4caf50', fontSize: '0.9rem' }}>
                  ✓ {selectedFile.name} uploaded
                </p>
              )}
            </div>
            <div className="config-field">
              <label>Zeichensatz:</label>
              <select
                value={config.encoding || 'windows-1252'}
                onChange={(e) => setConfig({ ...config, encoding: e.target.value })}
              >
                <option value="windows-1252">Windows (Windows-1252) - Standard für Excel</option>
                <option value="utf-8">UTF-8</option>
                <option value="iso-8859-1">ISO-8859-1 (Latin-1)</option>
                <option value="auto">Automatisch erkennen</option>
              </select>
            </div>
            <div className="config-field">
              <label>
                <input
                  type="checkbox"
                  checked={config.has_header !== false}
                  onChange={(e) => setConfig({ ...config, has_header: e.target.checked })}
                />
                Hat Kopfzeile
              </label>
            </div>
          </>
        );

      case 'excel':
        return (
          <>
            <div className="config-field">
              <label>Excel-Datei:</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={uploading}
                required
              />
              {uploading && <p style={{ color: '#667eea', fontSize: '0.9rem' }}>Uploading...</p>}
              {selectedFile && !uploading && (
                <p style={{ color: '#4caf50', fontSize: '0.9rem' }}>
                  ✓ {selectedFile.name} uploaded
                </p>
              )}
            </div>
            <div className="config-field">
              <label>Blattname:</label>
              {loadingSheets ? (
                <p style={{ color: '#667eea', fontSize: '0.9rem' }}>Lade Blätter...</p>
              ) : availableSheets.length > 0 ? (
                <select
                  value={config.sheet_name || ''}
                  onChange={(e) => setConfig({ ...config, sheet_name: e.target.value })}
                >
                  {availableSheets.map((sheet) => (
                    <option key={sheet} value={sheet}>{sheet}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={config.sheet_name || ''}
                  onChange={(e) => setConfig({ ...config, sheet_name: e.target.value })}
                  placeholder="Sheet1 (optional)"
                />
              )}
              {availableSheets.length > 1 && (
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  {availableSheets.length} Blätter gefunden
                </p>
              )}
            </div>
          </>
        );

      case 'database':
        return (
          <>
            <div className="config-field">
              <label>Datenbanktyp:</label>
              <select
                value={config.db_type || 'postgres'}
                onChange={(e) => setConfig({ ...config, db_type: e.target.value })}
              >
                <option value="postgres">PostgreSQL</option>
                <option value="mysql">MySQL</option>
                <option value="sqlite">SQLite</option>
              </select>
            </div>
            <div className="config-field">
              <label>Verbindungszeichenkette:</label>
              <input
                type="text"
                value={config.connection_string || ''}
                onChange={(e) => setConfig({ ...config, connection_string: e.target.value })}
                placeholder="host=localhost user=admin password=secret dbname=mydb"
                required
              />
            </div>
            <div className="config-field">
              <label>SQL-Abfrage:</label>
              <textarea
                value={config.query || ''}
                onChange={(e) => setConfig({ ...config, query: e.target.value })}
                placeholder="SELECT * FROM users WHERE id = 1"
                required
              />
            </div>
          </>
        );

      case 'api':
        return (
          <>
            <div className="config-field">
              <label>API-URL:</label>
              <input
                type="url"
                value={config.url || ''}
                onChange={(e) => setConfig({ ...config, url: e.target.value })}
                placeholder="https://api.example.com/data"
                required
              />
            </div>
            <div className="config-field">
              <label>Methode:</label>
              <select
                value={config.method || 'GET'}
                onChange={(e) => setConfig({ ...config, method: e.target.value })}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
              </select>
            </div>
            <div className="config-field">
              <label>Header (JSON, optional):</label>
              <textarea
                value={config.headers || ''}
                onChange={(e) => setConfig({ ...config, headers: e.target.value })}
                placeholder='{"Authorization": "Bearer token"}'
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="data-source-config">
      <h3>Datenvorlage konfigurieren</h3>
      <form onSubmit={handleSubmit}>
        <div className="config-field">
          <label>Name:</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Meine Datenvorlage"
            required
          />
        </div>

        <div className="config-field">
          <label>Typ:</label>
          <select
            value={sourceType}
            onChange={(e) => {
              setSourceType(e.target.value as any);
              setConfig({});
              setSelectedFile(null);
            }}
          >
            <option value="json">JSON-Datei</option>
            <option value="csv">CSV-Datei</option>
            <option value="excel">Excel-Datei</option>
            <option value="database">Datenbank</option>
            <option value="api">REST-API</option>
          </select>
        </div>

        {renderConfigFields()}

        {error && <div className="error">{error}</div>}

        <button type="submit" disabled={loading}>
          {loading ? 'Wird erstellt...' : 'Datenvorlage erstellen'}
        </button>
      </form>
    </div>
  );
};

export default DataSourceConfig;
